// The admin area's frame: seven sections, one gate, one place they are listed.
//
// WHO IT IS FOR — the two SeaState principals, not the client. Everything the
// console proper shows is one client's own data; this reads across clients,
// reports what the deployment is actually doing, and holds notes no client sees.
//
// THE GATE HERE IS COSMETIC AND THAT IS DELIBERATE. This decides whether the
// section renders; it decides nothing about access. Every /api/admin/* endpoint
// re-checks the role server-side on each request (server/admin.go), so a browser
// that lies about its role gets a page frame and no data. If this file were the
// only gate it would be no gate at all.
//
// ONE FILE PER SECTION, and each owns its own server file too. The sections were
// built in parallel; the contract that made that possible is that nothing here
// needs editing to add behaviour to a section.

import type { SiteNavOptions } from "../site.js";
import { mountAdminOverview } from "./overview.js";
import { mountAdminFeedback } from "./feedback.js";
import { mountAdminNotes } from "./notes.js";
import { mountAdminPeople } from "./people.js";
import { mountAdminRecord } from "./record.js";
import { mountAdminUsage } from "./usage.js";
import { mountAdminViewing } from "./viewing.js";

/** A section: its id in the URL hash, its label, and how to build it. */
export interface AdminSection {
  id: string;
  label: string;
  /** One line under the heading — what this section answers. */
  blurb: string;
  mount(host: HTMLElement): void;
}

export const ADMIN_SECTIONS: readonly AdminSection[] = [
  {
    id: "overview",
    label: "Overview",
    blurb: "What is deployed, how fresh the data is, and what needs attention",
    mount: mountAdminOverview,
  },
  {
    id: "console",
    label: "Console",
    blurb: "Which client's console you are looking at — switch between them here",
    mount: mountAdminViewing,
  },
  {
    id: "feedback",
    label: "Feedback",
    blurb: "What operators have reported, across every client",
    mount: mountAdminFeedback,
  },
  {
    id: "notes",
    label: "Notes",
    blurb: "Development notes between the principals",
    mount: mountAdminNotes,
  },
  {
    id: "usage",
    label: "Usage",
    blurb: "Are they turning up, and have they made the numbers theirs",
    mount: mountAdminUsage,
  },
  {
    id: "people",
    label: "People",
    blurb: "Accounts by client, when they were last seen, and access",
    mount: mountAdminPeople,
  },
  {
    id: "record",
    label: "Record",
    blurb: "Hours, the changelog with its commits, and the git log",
    mount: mountAdminRecord,
  },
];

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (className !== undefined) n.className = className;
  if (text !== undefined) n.textContent = text;
  return n;
}

function sectionFromHash(): AdminSection {
  const want = window.location.hash.replace(/^#/, "");
  return ADMIN_SECTIONS.find((s) => s.id === want) ?? (ADMIN_SECTIONS[0] as AdminSection);
}

export function mountAdmin(container: HTMLElement, opts: SiteNavOptions): void {
  document.title = "Admin — SeaState";

  // BEING SIGNED IN IS NOT THE CONDITION — being one of ours is. This asked only
  // `authed` at first, so any client account that typed /admin got the heading,
  // the lede and the names of all seven sections, with the data 404ing politely
  // behind them. The server gate was doing its job; the frame was the leak.
  //
  // Nothing is rendered and nothing is said. The router above already turns this
  // address into the front door for anyone else, so reaching here at all means a
  // caller forgot — and the answer to that is silence, not a notice confirming
  // there is something here to be refused from.
  if (!opts.authed || opts.staff !== true) {
    document.title = "SeaState";
    return;
  }

  const root = el("div", "admin");
  const head = el("div", "admin-head");
  head.append(
    el("h1", undefined, "Admin"),
    el("p", "admin-lede", "SeaState's own instruments. Not a client surface."),
  );
  root.appendChild(head);

  const tabs = el("nav", "admin-tabs");
  const host = el("div", "admin-host");
  const blurb = el("p", "admin-blurb");

  const paint = (): void => {
    const active = sectionFromHash();
    tabs.replaceChildren();
    for (const s of ADMIN_SECTIONS) {
      const a = el("a", "admin-tab" + (s.id === active.id ? " on" : ""), s.label);
      a.href = `#${s.id}`;
      tabs.appendChild(a);
    }
    blurb.textContent = active.blurb;
    host.replaceChildren();
    // A section that throws must not take the whole page with it — the Overview
    // in particular is the page you open when something is already wrong.
    try {
      active.mount(host);
    } catch (err) {
      console.error(`admin section ${active.id} failed`, err);
      host.replaceChildren(
        el("p", "note", `This section failed to load. The others still work.`),
      );
    }
  };

  root.append(tabs, blurb, host);
  container.appendChild(root);
  paint();
  window.addEventListener("hashchange", paint);
}
